import { Injectable, OnDestroy } from '@angular/core';
import { ITableData } from '../../../models/reports/ITableData';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { CommonDataService } from '../../common-data.service';
import { IReportData } from '../../../models/reports/IReportData';
import {
  EstimateMetaData, Escalations, Material, MaterialData, SQWebArea,
  SystemComponent, SubSystem, SystemChildren, Condition, LaborData, Task,
  SQWebSystem, TypicalOfSystemHelper
} from '../../../models/dtos';
import { TranslateService } from '@ngx-translate/core';
import { CONSTANTS } from '../../../Constants/Constants';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { IndexDBService } from '../../common/indexDB.service';
import { IDataItem } from '../../../models/reports/IDataItem';
import { IData } from '../../../models/reports/IData';
import { IAggregatedMaterialData } from '../../../models/reports/IAggregatedMaterialData';
import { ReportCheckboxes } from '../../../models/reports/ReportCheckboxes';
import { NetworkLaborData } from '../../../models/common/NetworkLaborData';
import { MaterialSummaryData } from '../../../models/reports/MaterialSummaryData';
import { LaborReportType, MaterialReportType, MiscProjectInformationType, TaxTypes } from '../../../models/enum';
import { MultilingualCurrencyPipe } from '../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe';
import { IFooter } from '../../../models/reports/IFooter';
import { SQWebAreaChildren } from '../../../models/SQWebAreaChildren';
import { MaterialIndicators } from '../../../models/MaterialIndicators';
import { ReportTextAlignment } from '../../../models/reports/ReportTextAlignment';
import { EscalationData } from '../../../models/dtos/Classes/EscalationData';
import { DecimalPrecisionPipe } from '../../../pipes/decimal-precision-pipe/decimal-precision.pipe';
import { roundToPrecision } from '../../../../types/number-extensions';
import { SolEstimateService } from '../../estimate/sol-estimate.service';
import { EstimateRowModel, EstimateLaborCostsModel } from '../../../models/sol-estimate-table/ui-models';
import type { LaborCategoryKey } from '../../../models/reports/LaborCategoryKey';

const MATERIAL_TYPE_RANK: Record<string, number> = {
  [MaterialReportType.Siemens_Material]: 1,
  [MaterialReportType.Outside_Material]: 2,
  [MaterialReportType.Mechanical_Material]: 3,
  [MaterialReportType.Electrical_Material]: 4
};

const REFERENCE_NUMBER_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base'
});

@Injectable()
export class SolCostReportService implements OnDestroy {
  costByPartNumberViewInfoList: ITableData[] = [];
  tableData: ITableData[] = [];
  private materialByTypeData: ITableData[] = [];
  private consolidatedMaterialSummaryData: Record<string, MaterialSummaryData> = {};
  private consolidatedLaborSummaryData: Record<string, MaterialSummaryData> = {};
  private consolidatedMiscSummaryData: Record<string, MaterialSummaryData> = {};
  private consolidatedEstimateSummaryData: Record<string, MaterialSummaryData> = {};
  private consolidatedSubcontractorSummaryData: Record<string, MaterialSummaryData> = {};
  private estimateMetaData!: EstimateMetaData;
  private projectAreas!: SQWebArea[];
  private projectMaterialData: Record<string, MaterialData> = {};
  private estimateTotalTable?: ITableData;
  selectedReportCheckboxes!: ReportCheckboxes;
  private typicalOfHelper: Record<string, TypicalOfSystemHelper> = {};
  private processedTypicalIds: Set<string> = new Set();

  private get escalationRates(): Escalations {
    return this.estimateMetaData.escalationRates;
  }

  private getTdcPercent(cost: number): number {
    const subtotalCost = this.solEstimateService.estimateModel.subTotals.cost;
    if (subtotalCost === CONSTANTS.ZERO) {
      return 0;
    }
    return (cost / subtotalCost) * CONSTANTS.HUNDRED;
  }
  private computeTotals(map: Record<string, MaterialSummaryData>) {
    let hours = 0;
    let cost = 0;
    let escalation = 0;
    let gm = 0;
    let sellPrice = 0;
    for (const item of Object.values(map)) {
      hours += item.hours ?? 0;
      cost += item.cost || 0;
      escalation += item.escalation || 0;
      gm += item.gm || 0;
      sellPrice += item.sellPrice || 0;
    }
    const tdcPercent = this.getTdcPercent(cost);
    return { tdcPercent, hours, cost, escalation, gm, sellPrice };
  }
  private isTotalsZero(total: { tdcPercent: number; hours: number; cost: number; escalation: number; gm: number; sellPrice: number }): boolean {
    return (
      (total.tdcPercent || 0) === 0 &&
      (total.hours || 0) === 0 &&
      (total.cost || 0) === 0 &&
      (total.escalation || 0) === 0 &&
      (total.gm || 0) === 0 &&
      (total.sellPrice || 0) === 0
    );
  }

  private shouldHideRow(data: MaterialSummaryData, type: 'labor' | 'risk' | 'summary'): boolean {
    if (!data) {
      return true;
    }
    const isZero = (value: number | undefined): boolean => (value ?? 0) === 0;
    return isZero(data.tdcPercent)
      && isZero(data.cost)
      && isZero(data.gm)
      && isZero(data.sellPrice)
      && (type !== 'labor' || isZero(data.hours))
      && (type === 'risk' || isZero(data.escalation));
  }

  private readonly globalAggregatedData = new Map<string, IAggregatedMaterialData>();
  private readonly networkLaborData: NetworkLaborData[] = [];
  private projectLaborCollection: Record<string, LaborData> = {};
  private selectedReportId: ReportList = 0 as ReportList;

  constructor(
    private readonly translate: TranslateService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly commonDataService: CommonDataService,
    private readonly indexDBService: IndexDBService,
    private readonly multiLingualPipe: MultilingualCurrencyPipe,
    private readonly decimalPrecisionPipe: DecimalPrecisionPipe,
    private readonly solEstimateService: SolEstimateService,
  ) { }

  async generateReport(reportId: ReportList, pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_LANDSCAPE, internalReport: boolean = true): Promise<void> {
    // Reset state
    this.globalAggregatedData.clear();
    this.costByPartNumberViewInfoList = [];
    this.processedTypicalIds.clear();
    this.selectedReportCheckboxes = this.reportsCommonService.getReportsCheckboxes();

    // Generate table data
    this.tableData = await this.generateTableData(reportId);

    // Determine orientation
    const effectiveOrientation: ReportPageOrientation =
      (reportId === ReportList.ZeroCostMaterialByPartNumberViewModel && this.selectedReportCheckboxes.expanded.checked) ||
        (reportId === ReportList.CostByPartNumberByTypeExpandedViewModel)
        ? CONSTANTS.REPORT_PAGEORIENTATION_LANDSCAPE
        : pageOrientation;

    const reportName = this.reportsCommonService.getSelectedReportName();
    const reportHeaderData = this.commonDataService.getReportHeaderData();
    const reportData: IReportData = {
      selectionName: this.translate.instant(reportName),
      reportTitle: this.translate.instant(reportName),
      reportTitles: {
        data: this.reportsCommonService.generateReportTitles(reportHeaderData)
      },
      tableData: this.tableData,
      pageOrientation: effectiveOrientation,
      internalReport: internalReport
    };
    this.reportsCommonService.setSelectedReport(reportData);
  }

  private async generateTableData(reportId: ReportList): Promise<ITableData[]> {
    if (!reportId) {
      return [];
    }

    switch (reportId) {
      case ReportList.EstimateSummaryReportViewModel:
        this.estimateMetaData = this.commonDataService.getEstimateMetaData();
        return this.generateMaterialByTypeReportData();
      case ReportList.CostByPartNumberByTypeExpandedViewModel:
        await this.prepareMaterialReportData(reportId);
        this.generateCostTablesByMaterialType();
        return this.costByPartNumberViewInfoList;
      case ReportList.CostByPartNumberExpandedViewModel:
        await this.prepareMaterialReportData(reportId);
        await this.costByPartNumberTableDataList();
        return this.costByPartNumberViewInfoList;
      case ReportList.ZeroCostMaterialByPartNumberViewModel:
        await this.prepareMaterialReportData(reportId);
        return await this.zeroCostMaterialByPartNumberTableData();
      default:
        return [];
    }
  }

  private async prepareMaterialReportData(reportId: ReportList): Promise<void> {
    this.selectedReportId = reportId;
    this.typicalOfHelper = this.commonDataService.getTypicalOfHelpers();
    const materialData = await this.indexDBService.getAllMaterialData();
    this.projectMaterialData = materialData.materialsCollection;
    this.projectAreas = this.commonDataService.getSQWebProject().areas;
    this.generateSequencedMaterialList();
  }

  private generateCostTablesByMaterialType(): void {
    if (this.globalAggregatedData.size === CONSTANTS.ZERO) {
      return;
    }
    const grouped = new Map<string, IAggregatedMaterialData[]>();

    for (const item of this.globalAggregatedData.values()) {
      const key = item.materialType === MaterialReportType.SI_OEM_Material
        || item.materialType === MaterialReportType.SI_OEM_Material_FR
        || item.materialType === MaterialReportType.Siemens_Material_FR
        ? MaterialReportType.Siemens_Material
        : item.materialType;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(item);
    }

    // Sort group keys (use Map.get rank instead of indexOf for O(1) lookups)
    const groupKeys = Array.from(grouped.keys()).sort((a, b) => {
      const rankA = MATERIAL_TYPE_RANK[a] ?? Number.MAX_SAFE_INTEGER;
      const rankB = MATERIAL_TYPE_RANK[b] ?? Number.MAX_SAFE_INTEGER;
      return rankA - rankB;
    });
    const allTableTotals: { materialType: string; total: number }[] = [];
    const titleRow = this.generateCostByPartNumberByTypeTitleRow(); // Cache title row - called once
    const expanded = this.selectedReportCheckboxes?.expanded?.checked ?? false;

    for (const groupName of groupKeys) {
      const items = grouped.get(groupName)!;
      items.sort((a, b) => this.compareReferenceNumbers(a.referenceNumber, b.referenceNumber));

      const table: ITableData = {
        tableHeader: [{
          headers: [{
            headerText: this.translate.instant(groupName === MaterialReportType.Siemens_Material ? 'SIEMENS_MATERIAL' : groupName),
            columnspan: expanded ? 10 : 8,
            alignment: CONSTANTS.LEFT_ALIGNMENT
          }]
        }],
        title: titleRow, // Use cached title row
        data: [],
        tableFooter: [],
        isTableBordered: true,
        isPageBreakAfter: false
      };

      // Single pass: build rows AND calculate total
      let groupTotal = CONSTANTS.ZERO;
      for (const item of items) {
        const itemTotal = item.quantity * item.cost;
        groupTotal += itemTotal;
        table.data.push({
          data: this.createCostByTypeRow(
            item.quantity,
            item.referenceNumber,
            item.modelNumber,
            item.manufacturerName,
            item.description,
            item.materialType,
            item.vendorName,
            item.notes,
            item.cost,
            itemTotal,
            item.materialIndicator
          )
        });
      }

      allTableTotals.push({ materialType: groupName, total: groupTotal });

      const emptyCells = expanded ? 8 : 6;
      const footerHeaders = [
        { headerText: this.translate.instant('TOTAL_COST'), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.LEFT_ALIGNMENT }
      ];
      for (let i = 0; i < emptyCells; i++) {
        footerHeaders.push({ headerText: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT });
      }
      footerHeaders.push({ headerText: this.multiLingualPipe.transform(groupTotal), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT });
      table.tableFooter.push({ headers: footerHeaders });
      this.costByPartNumberViewInfoList.push(table);
    }
    this.createFinalSummaryTable(allTableTotals);
  }

  createFinalSummaryTable(allTableTotals: { materialType: string; total: number }[]) {
    const summaryTable: ITableData = {
      tableHeader: [],
      title: [],
      data: [],
      tableFooter: [],
      isTableBordered: true,
      isPageBreakAfter: false
    };
    // Calculate grand total
    const grandTotal = allTableTotals.reduce((sum, item) => sum + item.total, CONSTANTS.ZERO);

    // Add footer with grand total
    summaryTable.tableFooter.push({
      headers: [
        {
          headerText: this.translate.instant('TAKE_OFF_TOTAL'),
          columnspan: CONSTANTS.ONE,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          headerText: this.multiLingualPipe.transform(grandTotal),
          columnspan: CONSTANTS.ONE,
          alignment: CONSTANTS.RIGHT_ALIGNMENT
        }
      ]
    });

    this.costByPartNumberViewInfoList.push(summaryTable);
  }


  private generateCostByPartNumberByTypeTitleRow(): IDataItem[] {
    const expanded = this.selectedReportCheckboxes?.expanded?.checked;
    const common = (label: string, width?: string): IDataItem => ({
      data: this.translate.instant(label),
      columnspan: CONSTANTS.ONE,
      hierarchyLevel: CONSTANTS.ZERO,
      alignment: CONSTANTS.CENTER_ALIGNMENT,
      ...(width ? { width } : {})
    });

    if (expanded) {
      return [
        common('Quantity'),
        common('REFERENCE_NUMBER', '80'),
        common('MODEL_#', '80'),
        common('MANUFACTURER_PART_#', '90'),
        common('DESCRIPTION', '150'),
        common('MATERIAL_TYPE'),
        common('VENDOR_NAME'),
        common('NOTES'),
        common('UNIT_COST'),
        common('TOTAL_COST')
      ];
    }

    return [
      common('Quantity'),
      common('REFERENCE_NUMBER', '80'),
      common('DESCRIPTION', '150'),
      common('MATERIAL_TYPE'),
      common('VENDOR_NAME'),
      common('NOTES'),
      common('UNIT_COST'),
      common('TOTAL_COST')
    ];
  }

  private compareReferenceNumbers(refA: string, refB: string): number {
    const a = (refA ?? CONSTANTS.EMPTY_STRING).trim();
    const b = (refB ?? CONSTANTS.EMPTY_STRING).trim();

    const hasLettersA = /[A-Za-z]/.test(a);
    const hasLettersB = /[A-Za-z]/.test(b);

    // Business sort preference: references without letters come first.
    // Example: "134-1504" should be ordered before "8EM13125CF180FA3".
    if (hasLettersA !== hasLettersB) {
      return hasLettersA ? 1 : -1;
    }

    return REFERENCE_NUMBER_COLLATOR.compare(a, b);
  }

  private createCostByTypeRow(quantity: number, referenceNumber: string,
    modelNumber: string, manufacturerName: string, description: string,
    materialType: string, vendorName: string, notes: string, cost: number,
    total: number, materialIndicator?: MaterialIndicators): IDataItem[] {
    const expanded = this.selectedReportCheckboxes?.expanded?.checked ?? false;
    const materialTypeDisplay = this.getMaterialType(materialType);
    const modelNumberDisplay = modelNumber ?? CONSTANTS.EMPTY_STRING;
    const manufacturerNameDisplay = manufacturerName ?? CONSTANTS.EMPTY_STRING;

    if (expanded) {
      return [
        this.createDataRow(quantity, CONSTANTS.CENTER_ALIGNMENT),
        this.createDataRow(referenceNumber, CONSTANTS.LEFT_ALIGNMENT, materialIndicator),
        this.createDataRow(modelNumberDisplay, CONSTANTS.LEFT_ALIGNMENT),
        this.createDataRow(manufacturerNameDisplay, CONSTANTS.LEFT_ALIGNMENT),
        this.createDataRow(description, CONSTANTS.LEFT_ALIGNMENT),
        this.createDataRow(materialTypeDisplay, CONSTANTS.LEFT_ALIGNMENT),
        this.createDataRow(vendorName, CONSTANTS.LEFT_ALIGNMENT),
        this.createDataRow(notes, CONSTANTS.LEFT_ALIGNMENT),
        this.createDataRow(this.multiLingualPipe.transform(cost), CONSTANTS.RIGHT_ALIGNMENT),
        { ...this.createDataRow(this.multiLingualPipe.transform(total), CONSTANTS.RIGHT_ALIGNMENT), DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR }
      ];
    }

    return [
      this.createDataRow(quantity, CONSTANTS.CENTER_ALIGNMENT),
      this.createDataRow(referenceNumber, CONSTANTS.LEFT_ALIGNMENT, materialIndicator),
      this.createDataRow(description, CONSTANTS.LEFT_ALIGNMENT),
      this.createDataRow(materialTypeDisplay, CONSTANTS.LEFT_ALIGNMENT),
      this.createDataRow(vendorName, CONSTANTS.LEFT_ALIGNMENT),
      this.createDataRow(notes, CONSTANTS.LEFT_ALIGNMENT),
      this.createDataRow(this.multiLingualPipe.transform(cost), CONSTANTS.RIGHT_ALIGNMENT),
      { ...this.createDataRow(this.multiLingualPipe.transform(total), CONSTANTS.RIGHT_ALIGNMENT), DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR }
    ];

  }

  private createDataRow(data: number | string, alignment: ReportTextAlignment, materialIndicator?: MaterialIndicators): IDataItem {
    return {
      data: data.toString(),
      columnspan: CONSTANTS.ONE,
      hierarchyLevel: CONSTANTS.ZERO,
      alignment: alignment,
      ...(materialIndicator ? { materialIndicator } : {})
    };
  }

  async zeroCostMaterialByPartNumberTableData(): Promise<ITableData[]> {
    const expanded = this.selectedReportCheckboxes.expanded.checked;
    // Sort alphanumerically by referenceNumber (case-insensitive, natural numeric)
    const sortedItems = Array.from(this.globalAggregatedData.values()).sort((a, b) =>
      (a.referenceNumber || '').localeCompare(b.referenceNumber || '', undefined, { numeric: true, sensitivity: 'base' })
    );

    const systemRows: IData[] = [];
    for (const child of sortedItems) {
      if (expanded) {
        systemRows.push({
          data: [
            { data: child.referenceNumber, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT, materialIndicator: child.materialIndicator },
            {
              data: child.modelNumber ?? CONSTANTS.EMPTY_STRING, isSubHeader: false, columnspan: 1,
              hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT
            },
            {
              data: child.mpn ?? CONSTANTS.EMPTY_STRING, isSubHeader: false, columnspan: 1,
              hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT
            },
            { data: child.description, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT },
            { data: child.quantity.toString(), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.CENTER_ALIGNMENT },
            { data: this.multiLingualPipe.transform(child.cost), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.RIGHT_ALIGNMENT }
          ]
        });
      } else {
        systemRows.push({
          data: [
            this.createDataRow(child.referenceNumber, CONSTANTS.LEFT_ALIGNMENT, child.materialIndicator),
            this.createDataRow(child.description, CONSTANTS.LEFT_ALIGNMENT),
            this.createDataRow(child.quantity, CONSTANTS.CENTER_ALIGNMENT),
            this.createDataRow(this.multiLingualPipe.transform(child.cost), CONSTANTS.RIGHT_ALIGNMENT)
          ]
        });
      }
    }

    if (systemRows.length > CONSTANTS.ZERO) {
      const title = expanded
        ? [
          { data: this.translate.instant('MATERIAL_REF_NUMBER'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR },
          { data: this.translate.instant('MODEL_NUMBER'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
          { data: this.translate.instant('MANUFACTURER_PART_NUMBER'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0},
          { data: this.translate.instant('DESCRIPTION'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, width: '250' },
          { data: this.translate.instant('Quantity'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
          { data: this.translate.instant('UNIT_COST'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 }
        ]
        : [
          { data: this.translate.instant('MATERIAL_REF_NUMBER'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, width: '100', DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR },
          { data: this.translate.instant('DESCRIPTION'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, width: '250' },
          { data: this.translate.instant('Quantity'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
          { data: this.translate.instant('UNIT_COST'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 }
        ];

      return [{
        tableHeader: [],
        title,
        data: systemRows,
        tableFooter: [],
        isPageBreakAfter: false,
        isTableBordered: true
      }];
    }
    return [];
  }

  private buildDuplicateKey(child: SystemComponent, expanded: boolean): string {
    const mat = child as Material;
    const description = child.description ?? CONSTANTS.EMPTY_STRING;
    return expanded
      ? `${child.referenceNumber}_${mat.modelNumber ?? CONSTANTS.EMPTY_STRING}_${mat.manufacturerName ?? CONSTANTS.EMPTY_STRING}_${description}`
      : `${child.referenceNumber}_${description}`;
  }

  //#region COST_SUMMARY Report Methods

  private generateMaterialByTypeReportData(): ITableData[] {
    this.materialByTypeData = [];
    this.estimateTotalTable = undefined;
    this.getMaterialInformation();
    this.getLaborInformation();
    this.getSubcontractorInformation();
    this.getMiscProjectInformation();
    this.getEstimateTotal();
    return [...this.materialByTypeData];
  }

  private generateSequencedMaterialList(): void {
    if (!this.projectAreas || this.projectAreas.length === 0) {
      return;
    }
    for (const area of this.projectAreas) {
      this.iterateArea(area);
    }
  }

  private iterateArea(area: SQWebArea): void {
    if (!area || !area.isItemVisible) {
      return;
    }

    for (const areaChild of area.children as SQWebAreaChildren[]) {
      if (!areaChild.nodeType || !areaChild.itemUniqueId) {
        continue;
      }

      switch (areaChild.nodeType) {
        case CONSTANTS.NODETYPE_AREA:
          this.iterateArea(areaChild as SQWebArea);
          break;
        case CONSTANTS.NODETYPE_BSETSYSTEM:
          this.handleTypicalOf(areaChild as SQWebSystem);
          break;
        default:
          this.formatReportData(areaChild.itemUniqueId);
          break;
      }
    }
  }

  handleTypicalOf(system: SQWebSystem) {
    if(!system.isItemVisible){
      return;
    }
    if (system.hasTypicalOf) {
      const typicalId = system.systemTypicalOfHelperUniqueId;
      const typicalOf = this.typicalOfHelper[typicalId];
      const typicalOfCount = typicalOf?.typicalOfSystems?.filter(typicalSystem => typicalSystem.isItemVisible).length ?? CONSTANTS.ONE;
      const typicalOfUniqueId = typicalOf?.typicalOfCollectionHolderId;
      if (!this.processedTypicalIds.has(typicalId)) {
        this.processedTypicalIds.add(typicalId);
        this.formatReportData(typicalOfUniqueId, typicalOfCount);
      }
    } else {
      this.formatReportData(system.itemUniqueId);
    }
  }


  private formatReportData(materialUniqueId: string, typicalOfCount?: number): void {
    if (!materialUniqueId || !this.projectMaterialData?.[materialUniqueId]) {
      return;
    }

    const materialItemsCollection = this.projectMaterialData[materialUniqueId].systemChildrenCollection;
    if (!materialItemsCollection) {
      return;
    }

    this.formatMaterialData(materialItemsCollection, typicalOfCount);
  }

  private formatMaterialData(materialItemsCollection: SystemChildren, typicalOfCount?: number): void {
    if (!materialItemsCollection) {
      return;
    }
    materialItemsCollection?.forEach((materialItem) => {
      if (materialItem.systemComponentNodeType === CONSTANTS.NODETYPE_SUBSYSTEM) {
        (materialItem as SubSystem).children.forEach((childMaterialItem) => {
          this.generateMaterialReportData(childMaterialItem as Material, typicalOfCount);
        });
      } else {
        this.generateMaterialReportData(materialItem as Material, typicalOfCount);
      }
    });
  }

  private getDescription(materialItem: Material): string {
    if (this.reportsCommonService.isGenericNameApplicable()) {
      return this.reportsCommonService.getGenericNameBySSN(materialItem.ssn) || materialItem.description;
    }
    return materialItem.description;
  }

  private generateMaterialReportData(materialItem: Material, typicalOfCount?: number): void {
    if (!materialItem) {
      return;
    }

    switch (this.selectedReportId) {
      case ReportList.CostByPartNumberByTypeExpandedViewModel:
      case ReportList.CostByPartNumberExpandedViewModel:
        if (materialItem.cost <= CONSTANTS.ZERO || materialItem.quantity <= CONSTANTS.ZERO || !materialItem.isMaster) {
          return;
        }
        const globalAggregateKey = `${materialItem.referenceNumber}_${materialItem.cost}`;
        this.generateGlobalAggregateData(materialItem, globalAggregateKey, typicalOfCount);
        break;
      case ReportList.ZeroCostMaterialByPartNumberViewModel:
        if (materialItem.cost > CONSTANTS.ZERO || materialItem.quantity <= CONSTANTS.ZERO || !materialItem.isMaster) {
          return;
        }
        const expanded = this.selectedReportCheckboxes.expanded.checked;
        const globalZeroCostKey = this.buildDuplicateKey(materialItem, expanded);
        this.generateGlobalAggregateData(materialItem, globalZeroCostKey, typicalOfCount);
        break;
      default:
        break;
    }
  }

  generateGlobalAggregateData(materialItem: Material, key: string, typicalOfCount?: number): void {
    const globalExisting = this.globalAggregatedData.get(key);
    const quantity = (typicalOfCount ?? CONSTANTS.ONE) * materialItem.quantity;
    if (globalExisting) {
      globalExisting.quantity += quantity;
    } else {
      this.globalAggregatedData.set(key, {
        referenceNumber: materialItem.referenceNumber,
        description: this.getDescription(materialItem),
        quantity,
        cost: materialItem.cost,
        materialType: materialItem.materialType,
        notes: materialItem.notes,
        vendorName: materialItem.vendorName,
        modelNumber: materialItem.modelNumber,
        manufacturerName: materialItem.mpn,
        materialIndicator: this.reportsCommonService.generateMaterialIndicator(materialItem),
        mpn: materialItem.mpn
      });
    }
  }

  //#region Material Information
  private getMaterialInformation(): void {
    const summaryMaterialDataRows: IData[] = this.formatSummaryMaterialDataRows();
    const totalsForHide = this.computeTotals(this.consolidatedMaterialSummaryData);
    if (summaryMaterialDataRows.length === 0 || this.isTotalsZero(totalsForHide)) {
      return;
    }
    const summaryMaterialData: ITableData = {
      title: this.buildSectionTitleRow(
        'MATERIAL_REPORT_MATERIAL',
        CONSTANTS.EMPTY_STRING,
        this.translate.instant('MATERIAL_REPORT_ESCALATION')
      ),
      data: summaryMaterialDataRows,
      tableFooter: this.summaryTableFooter(),
      isTableBordered: true
    };
    this.materialByTypeData.push(summaryMaterialData);
  }

  private summaryTableFooter(): IFooter[] {
    const rowTotals = this.computeTotals(this.consolidatedMaterialSummaryData);
    const totals = {
      ...this.summarizeEstimateRows(CONSTANTS.EMPTY_STRING, [this.solEstimateService.estimateModel.materialCosts.materialCostsHeader]),
      hours: rowTotals.hours,
      escalation: rowTotals.escalation
    };
    return [
      this.buildFooterRow(
        this.translate.instant('TOTAL_MATERIAL'),
        totals,
        CONSTANTS.EMPTY_STRING,
        this.multiLingualPipe.transform(totals.escalation)
      )
    ];
  }

  private formatSummaryMaterialDataRows(): IData[] {
    const summaryMaterialDataRows: IData[] = [];
    this.consolidatedMaterialSummaryData = {};

    this.addMaterialSummaryRow(MaterialReportType.Siemens_Material, 'siemensMaterial', summaryMaterialDataRows);
    this.addMaterialSummaryRow(MaterialReportType.Outside_Material, 'outsideMaterial', summaryMaterialDataRows);
    this.addMaterialSummaryRow(MaterialReportType.Mechanical_Material, 'mechanicalMaterial', summaryMaterialDataRows);
    this.addMaterialSummaryRow(MaterialReportType.Electrical_Material, 'electricalMaterial', summaryMaterialDataRows);

    this.addMaterialSummaryRow(MaterialReportType.SHIPPING_HANDLING, 'shippingHandling', summaryMaterialDataRows);
    this.addMaterialSummaryRow(MaterialReportType.WARRANTY, 'warranty', summaryMaterialDataRows);

    return summaryMaterialDataRows;
  }

  private addMaterialSummaryRow(
    materialReportType: string,
    consolidatedKey: string,
    dataRows: IData[]
  ): void {
    const summaryData = this.calculateCostTdcEscalation(materialReportType);
    if (!this.shouldHideRow(summaryData, 'summary')) {
      this.consolidatedMaterialSummaryData[consolidatedKey] = summaryData;
      dataRows.push(this.buildSummaryRow(summaryData));
    }
  }

  private calculateCostTdcEscalation(material: string): MaterialSummaryData {
    const materials = this.solEstimateService.estimateModel.materialCosts;
    const categories = [materials.siemensMaterial, materials.outsideMaterial, materials.mechanicalMaterial, materials.electricalMaterial];
    let rows: EstimateRowModel[] = [];
    let label = CONSTANTS.EMPTY_STRING;
    let escalation = 0;
    let escalationData: EscalationData | undefined;
    switch (material) {
      case MaterialReportType.Siemens_Material:
        rows = [materials.siemensMaterial.headerRow];
        label = 'MATERIAL_REPORT_SIEMENS_MATERIAL';
        escalationData = this.escalationRates?.siemensMaterialEscalation;
        break;
      case MaterialReportType.Outside_Material:
        rows = [materials.outsideMaterial.headerRow];
        label = 'MATERIAL_REPORT_OUTSIDE_MATERIAL';
        escalationData = this.escalationRates?.outsideMaterialEscalation;
        break;
      case MaterialReportType.Mechanical_Material:
        rows = [materials.mechanicalMaterial.headerRow];
        label = 'MATERIAL_REPORT_MECHANICAL_MATERIAL';
        escalationData = this.escalationRates?.mechanicalMaterialEscalation;
        break;
      case MaterialReportType.Electrical_Material:
        rows = [materials.electricalMaterial.headerRow];
        label = 'MATERIAL_REPORT_ELECTRICAL_MATERIAL';
        escalationData = this.escalationRates?.electricalMaterialEscalation;
        break;
      case MaterialReportType.SHIPPING_HANDLING:
        rows = categories.map(category => category.shippingAndHandling);
        label = 'MATERIAL_REPORT_SHIPPING_HANDLING';
        break;
      case MaterialReportType.WARRANTY:
        rows = categories.map(category => category.warranty);
        label = 'MATERIAL_REPORT_WARRANTY';
        break;
      default:
        break;
    }
    if (rows.some(row => row.cost !== CONSTANTS.ZERO) && escalationData?.escalationEnabled) {
      escalation = escalationData.escalatedCost - escalationData.baseCost;
    }
    return { ...this.summarizeEstimateRows(label, rows), escalation };
  }

  private summarizeEstimateRows(label: string, rows: EstimateRowModel[]): MaterialSummaryData {
    const cost = rows.reduce((total, row) => total + row.cost, CONSTANTS.ZERO);
    return {
      materialName: label ? this.translate.instant(label) : CONSTANTS.EMPTY_STRING,
      cost,
      tdcPercent: this.getTdcPercent(cost),
      escalation: CONSTANTS.ZERO,
      gm: rows.reduce((total, row) => total + row.grossMargin, CONSTANTS.ZERO),
      sellPrice: rows.reduce((total, row) => total + row.sellPrice, CONSTANTS.ZERO)
    };
  }

  //#endregion

  //#region Labor Information
  getLaborInformation() {
    const laborDataRows: IData[] = this.formatLabourMaterialDataRows();
    const totalsForHide = this.computeTotals(this.consolidatedLaborSummaryData);
    if (laborDataRows.length === CONSTANTS.ZERO || this.isTotalsZero(totalsForHide)) {
      return;
    }
    const laborData: ITableData = {
      title: this.buildSectionTitleRow(
        'MATERIAL_REPORT_LABOR',
        this.translate.instant('MATERIAL_REPORT_HOURS'),
        this.translate.instant('MATERIAL_REPORT_ESCALATION')
      ),
      data: laborDataRows,
      tableFooter: this.laborFooterData(),
      isTableBordered: true
    };
    this.materialByTypeData.push(laborData);
  }

  laborFooterData(): IFooter[] {
    const rowTotals = this.computeTotals(this.consolidatedLaborSummaryData);
    const visibleHours = Object.values(this.consolidatedLaborSummaryData).reduce(
      (hours, row) => row.sellPrice === CONSTANTS.ZERO ? hours : hours + (row.hours ?? CONSTANTS.ZERO),
      CONSTANTS.ZERO
    );
    const totals = {
      ...this.summarizeEstimateRows(CONSTANTS.EMPTY_STRING, [this.solEstimateService.estimateModel.laborCosts.laborCostsHeader]),
      hours: visibleHours,
      escalation: rowTotals.escalation
    };
    return [
      this.buildFooterRow(
        this.translate.instant('TOTAL_LABOR'),
        totals,
        this.decimalPrecisionPipe.transform(roundToPrecision(totals.hours, this.commonDataService.getDecimalPrecision())),
        this.multiLingualPipe.transform(totals.escalation)
      )
    ];
  }

  formatLabourMaterialDataRows(): IData[] {
    const summaryLaborDataRows: IData[] = [];
    this.consolidatedLaborSummaryData = {};

    this.addLaborSummaryRow(LaborReportType.Project_Management_Labor, 'projectManagementLabor', summaryLaborDataRows);
    this.addLaborSummaryRow(LaborReportType.Engineer_Labor, 'engineerLabor', summaryLaborDataRows);
    this.addLaborSummaryRow(LaborReportType.CENG_Labor, 'cEngineering', summaryLaborDataRows);
    this.addLaborSummaryRow(LaborReportType.EENG_Labor, 'eEngineering', summaryLaborDataRows);
    this.addLaborSummaryRow(LaborReportType.LENG_Labor, 'lEngineering', summaryLaborDataRows);
    this.addLaborSummaryRow(LaborReportType.NENG_Labor, 'nEngineering', summaryLaborDataRows);
    this.addLaborSummaryRow(LaborReportType.QENG_Labor, 'qEngineering', summaryLaborDataRows);
    this.addLaborSummaryRow(LaborReportType.Programming_Labor, 'programmingLabor', summaryLaborDataRows);
    this.addLaborSummaryRow(LaborReportType.Graphics_Labor, 'graphicsLabor', summaryLaborDataRows);
    this.addLaborSummaryRow(LaborReportType.Specialist_Labor, 'specialistLabor', summaryLaborDataRows);
    this.addLaborSummaryRow(LaborReportType.Digital_Deployment_Labor, 'digitalDeploymentLabor', summaryLaborDataRows);
    this.addLaborSummaryRow(LaborReportType.Mechanical_Engineer_Labor, 'mechanicalEngineerLabor', summaryLaborDataRows);
    this.addLaborSummaryRow(LaborReportType.Electrician_Engineer_Labor, 'electricianEngineerLabor', summaryLaborDataRows);

    return summaryLaborDataRows;
  }

  private addLaborSummaryRow(
    laborType: LaborReportType,
    consolidatedKey: string,
    dataRows: IData[]
  ): void {
    const summaryData = this.calculateCostTdcEscalationLabor(laborType);
    if (!this.shouldHideRow(summaryData, 'labor')) {
      this.consolidatedLaborSummaryData[consolidatedKey] = summaryData;
      if (summaryData.sellPrice !== CONSTANTS.ZERO) {
        dataRows.push(this.buildLaborRow(summaryData));
      }
    }
  }

  private readonly LABOR_CONFIG: Partial<Record<LaborReportType, {
    row: Exclude<keyof EstimateLaborCostsModel, 'laborCostsHeader'>;
    category: LaborCategoryKey;
    escalationKey: keyof Escalations;
    labelKey: string;
  }>> = {
    [LaborReportType.Project_Management_Labor]: {
      row: 'projectManagement',
      category: 'projectManagerCategory',
      escalationKey: 'pmLaborEscalation',
      labelKey: 'MATERIAL_REPORT_PROJECT_MANAGEMENT_LABOR'
    },
    [LaborReportType.Engineer_Labor]: {
      row: 'engineering',
      category: 'engineerCategory',
      escalationKey: 'engLaborEscalation',
      labelKey: 'MATERIAL_REPORT_ENGINEERING_LABOR'
    },
    [LaborReportType.Programming_Labor]: {
      row: 'programming',
      category: 'programmingCategory',
      escalationKey: 'progLaborEscalation',
      labelKey: 'MATERIAL_REPORT_PROGRAMMING_LABOR'
    },
    [LaborReportType.Graphics_Labor]: {
      row: 'graphics',
      category: 'graphicsCategory',
      escalationKey: 'grapLaborEscalation',
      labelKey: 'MATERIAL_REPORT_GRAPHICS_LABOR'
    },
    [LaborReportType.Specialist_Labor]: {
      row: 'specialist',
      category: 'specialistCategory',
      escalationKey: 'specLaborEscalation',
      labelKey: 'MATERIAL_REPORT_SPECIALIST_LABOR'
    },
    [LaborReportType.Digital_Deployment_Labor]: {
      row: 'digitalDeploymentSpecialist',
      category: 'ddsCategory',
      escalationKey: 'ddepLaborEscalation',
      labelKey: 'MATERIAL_REPORT_DIGITAL_DEPLOYMENT_LABOR'
    },
    [LaborReportType.Mechanical_Engineer_Labor]: {
      row: 'mechanical',
      category: 'mechanicalCategory',
      escalationKey: 'mechLaborEscalation',
      labelKey: 'MATERIAL_REPORT_MECHANICAL_ENGINEER_LABOR'
    },
    [LaborReportType.Electrician_Engineer_Labor]: {
      row: 'electrical',
      category: 'electricalCategory',
      escalationKey: 'elecLaborEscalation',
      labelKey: 'MATERIAL_REPORT_ELECTRICAL_ENGINEER_LABOR'
    },
    [LaborReportType.CENG_Labor]: {
      row: 'cyberSecurityEngineering',
      category: 'cyberSecEngineerCategory',
      escalationKey: 'cengLaborEscalation',
      labelKey: 'CYBER_SECURITY_ENGINEERING'
    },
    [LaborReportType.EENG_Labor]: {
      row: 'energyEngineering',
      category: 'energyEngineerCategory',
      escalationKey: 'eengLaborEscalation',
      labelKey: 'ENERGY_ENGINEERING'
    },
    [LaborReportType.LENG_Labor]: {
      row: 'electricalEngineering',
      category: 'lengEngineerCategory',
      escalationKey: 'lengLaborEscalation',
      labelKey: 'ELECTRICAL_ENGINEERING'
    },
    [LaborReportType.NENG_Labor]: {
      row: 'networkEngineering',
      category: 'networkEngineerCategory',
      escalationKey: 'nengLaborEscalation',
      labelKey: 'NETWORK_ENGINEERING'
    },
    [LaborReportType.QENG_Labor]: {
      row: 'qualificationEngineering',
      category: 'qualificationEngineerCategory',
      escalationKey: 'qengLaborEscalation',
      labelKey: 'QUALIFICATION_ENGINEERING'
    }
  };
  calculateCostTdcEscalationLabor(material: LaborReportType): MaterialSummaryData {
    const emptyResult = { materialName: CONSTANTS.EMPTY_STRING, cost: 0, tdcPercent: 0, escalation: 0, gm: 0, sellPrice: 0, hours: 0 };
    const config = this.LABOR_CONFIG[material];
    if (!config) {
      return emptyResult;
    }
    const row = this.solEstimateService.estimateModel.laborCosts[config.row];
    const project = this.commonDataService.getSQWebProject();
    const category = project?.[config.category];
    const esc = this.escalationRates?.[config.escalationKey] as EscalationData | undefined;
    const escalation = row.cost !== CONSTANTS.ZERO && esc?.escalationEnabled ? esc.escalatedCost - esc.baseCost : CONSTANTS.ZERO;
    return {
      ...this.summarizeEstimateRows(config.labelKey, [row]),
      hours: category?.hours ?? CONSTANTS.ZERO,
      escalation
    };
  }

  //#endregion

  //#region Subcontractor Information
  getSubcontractorInformation() {
    const subcontractorDataRows: IData[] = this.formatSubcontractorMaterialDataRows();
    const totalsForHide = this.computeTotals(this.consolidatedSubcontractorSummaryData);
    if (subcontractorDataRows.length === 0 || this.isTotalsZero(totalsForHide)) {
      return;
    }
    const subcontractorData: ITableData = {
      title: this.buildSectionTitleRow('MATERIAL_REPORT_SUBCONTRACTORS', CONSTANTS.EMPTY_STRING, CONSTANTS.EMPTY_STRING),
      data: subcontractorDataRows,
      tableFooter: this.subcontractorFooterData(),
      isTableBordered: true
    };
    this.materialByTypeData.push(subcontractorData);
  }

  formatSubcontractorMaterialDataRows(): IData[] {
    const summarySubcontractorDataRows: IData[] = [];
    this.consolidatedSubcontractorSummaryData = {};
    this.solEstimateService.estimateModel.subContractorCosts.subContractorsCostsRow.forEach((sub, idx) => {
      const cost = sub.cost || 0;
      const sellPrice = sub.sellPrice || 0;
      const sd: MaterialSummaryData = {
        materialName: sub.name || CONSTANTS.EMPTY_STRING,
        cost,
        hours: 0,
        tdcPercent: this.getTdcPercent(cost),
        escalation: 0,
        gm: sub.grossMargin,
        sellPrice
      };

      if (!this.shouldHideRow(sd, 'risk')) {
        const key = `subcontractor_${idx}`;
        this.consolidatedSubcontractorSummaryData[key] = sd;
        summarySubcontractorDataRows.push(this.buildRiskRow(sd));
      }
    });
    return summarySubcontractorDataRows;
  }

  subcontractorFooterData(): IFooter[] {
    const totals = this.summarizeEstimateRows(CONSTANTS.EMPTY_STRING, [this.solEstimateService.estimateModel.subContractorCosts.headerRow]);
    return [
      this.buildFooterRow(
        this.translate.instant('TOTAL_SUBCONTRACTOR_COST'),
        totals,
        CONSTANTS.EMPTY_STRING,
        CONSTANTS.EMPTY_STRING
      )
    ];
  }
  //#endregion

  //#region Misc Project Information
  getMiscProjectInformation() {
    const miscDataRows: IData[] = this.formatMiscMaterialDataRows();
    const totalsForHide = this.computeTotals(this.consolidatedMiscSummaryData);
    if (miscDataRows.length === 0 || this.isTotalsZero(totalsForHide)) {
      return;
    }
    const miscData: ITableData = {
      title: this.buildSectionTitleRow('MATERIAL_REPORT_MISCELLANEOUS', CONSTANTS.EMPTY_STRING, CONSTANTS.EMPTY_STRING),
      data: miscDataRows,
      tableFooter: this.miscFooterData(),
      isTableBordered: true
    };
    this.materialByTypeData.push(miscData);
  }

  miscFooterData(): IFooter[] {
    const model = this.solEstimateService.estimateModel;
    const totals = this.summarizeEstimateRows(CONSTANTS.EMPTY_STRING, [model.miscRiskTravelCosts.headerRow, model.technologyFee]);
    const getSapValues = this.getSapCostInformation();
    return [
      this.buildFooterRow(
        this.translate.instant('TOTAL_MISCELLANEOUS'),
        totals,
        CONSTANTS.EMPTY_STRING,
        CONSTANTS.EMPTY_STRING
      ),
      {
        headers: [
          { headerText: this.translate.instant('SAP_MISC_DOLLARS'), columnspan: 1 },
          { headerText: CONSTANTS.EMPTY_STRING, columnspan: 1, alignment: CONSTANTS.RIGHT_ALIGNMENT },
          { headerText: CONSTANTS.EMPTY_STRING, columnspan: 1, alignment: CONSTANTS.RIGHT_ALIGNMENT },
          { headerText: this.multiLingualPipe.transform(getSapValues.cost), columnspan: 1, alignment: CONSTANTS.RIGHT_ALIGNMENT },
          { headerText: CONSTANTS.EMPTY_STRING, columnspan: 1, alignment: CONSTANTS.RIGHT_ALIGNMENT },
          { headerText: CONSTANTS.EMPTY_STRING, columnspan: 1, alignment: CONSTANTS.RIGHT_ALIGNMENT },
          { headerText: this.multiLingualPipe.transform(getSapValues.sellPrice), columnspan: 1, alignment: CONSTANTS.RIGHT_ALIGNMENT }
        ]
      }
    ]
  }

  formatMiscMaterialDataRows(): IData[] {
    const summaryMiscDataRows: IData[] = [];
    this.consolidatedMiscSummaryData = {};

    this.addMiscSummaryRow(MiscProjectInformationType.Miscellaneous_Cost, 'miscellaneousCosts', summaryMiscDataRows);
    this.addMiscSummaryRow(MiscProjectInformationType.Productivity_Reserve, 'productivityReserveCost', summaryMiscDataRows);
    this.addMiscSummaryRow(MiscProjectInformationType.Risk_Cost, 'riskCost', summaryMiscDataRows);
    this.addMiscSummaryRow(MiscProjectInformationType.Subcontractor_Fee, 'subcontractorFee', summaryMiscDataRows);
    this.addMiscSummaryRow(MiscProjectInformationType.Travel_Expense, 'travelExpense', summaryMiscDataRows);

    return summaryMiscDataRows;
  }

  private addMiscSummaryRow(
    miscType: MiscProjectInformationType,
    consolidatedKey: string,
    dataRows: IData[]
  ): void {
    const summaryData = this.calculateCostTdcEscalationMisc(miscType);
    if (!this.shouldHideRow(summaryData, 'risk')) {
      this.consolidatedMiscSummaryData[consolidatedKey] = summaryData;
      dataRows.push(this.buildRiskRow(summaryData));
    }
  }

  calculateCostTdcEscalationMisc(material: MiscProjectInformationType): MaterialSummaryData {
    let label = CONSTANTS.EMPTY_STRING;
    let names: string[] = [];
    const model = this.solEstimateService.estimateModel;
    switch (material) {
      case MiscProjectInformationType.Miscellaneous_Cost:
        label = 'MATERIAL_REPORT_MISCELLANEOUS_COST';
        names = ['MISCELLANEOUS_COSTS', 'MECHANICAL_FEE'];
        break;
      case MiscProjectInformationType.Productivity_Reserve:
        label = 'MATERIAL_REPORT_PRODUCTIVITY_RESERVE';
        names = ['PRODUCTIVITY_RESERVE'];
        break;
      case MiscProjectInformationType.Risk_Cost:
        label = 'MATERIAL_REPORT_RISK_COST';
        names = ['PROJECT_ENTERED_RISK_COSTS', 'CALCULATED_RISK_COSTS'];
        break;
      case MiscProjectInformationType.Subcontractor_Fee:
        label = 'MATERIAL_REPORT_SUBCONTRACTOR_FEE';
        names = ['SUBCONTRACTOR_FEE'];
        break;
      case MiscProjectInformationType.Travel_Expense:
        label = 'MATERIAL_REPORT_TRAVEL_EXPENSE';
        names = ['TRAVEL_EXPENSE_COSTS'];
        break;
      default:
        break;
    }
    const rows: EstimateRowModel[] = [
      ...model.miscRiskTravelCosts.rows,
      ...(model.miscRiskTravelCosts.riskCosts?.rows ?? [])
    ].filter(row => names.includes(row.name));
    if (material === MiscProjectInformationType.Miscellaneous_Cost) {
      rows.push(model.technologyFee);
    }
    return this.summarizeEstimateRows(label, rows);
  }
  //#endregion

  //#region SAP Cost Information
  getSapCostInformation() {
    const totals = [MiscProjectInformationType.Miscellaneous_Cost,
      MiscProjectInformationType.Subcontractor_Fee, MiscProjectInformationType.Travel_Expense]
      .map(type => this.calculateCostTdcEscalationMisc(type));
    return {
      cost: totals.reduce((total, row) => total + row.cost, CONSTANTS.ZERO),
      sellPrice: totals.reduce((total, row) => total + row.sellPrice, CONSTANTS.ZERO)
    };
  }
  //#endregion

  //#region Estimate Total Information
  getEstimateTotal() {
    if (!this.estimateMetaData?.estimateTab) {
      return;
    }
    this.getEstimateTotalInformation();
    const model = this.solEstimateService.estimateModel;
    const isSalesTax = this.estimateMetaData.estimateSettings?.taxTypeID === TaxTypes.SalesTax;
    const taxRow = isSalesTax ? model.taxMaterialLaborRate.headerRow : model.taxMaterialRate.headerRow;
    this.getTaxRow(isSalesTax ? taxRow.sellPrice : taxRow.cost);
  }

  getTaxRow(taxCost: number) {
    if (!this.estimateMetaData?.estimateTab) {
      return;
    }
    if (taxCost === 0) {
      return;
    }

    const isSalesTax = this.estimateMetaData.estimateSettings?.taxTypeID === TaxTypes.SalesTax;
    const model = this.solEstimateService.estimateModel;
    const taxRowModel = isSalesTax ? model.taxMaterialLaborRate.headerRow : model.taxMaterialRate.headerRow;
    const cost = isSalesTax ? CONSTANTS.ZERO : taxRowModel.cost;
    const escalation = 0;
    const sellPrice = taxRowModel.sellPrice;
    const gm = CONSTANTS.ZERO;
    const tdcPercent = this.getTdcPercent(cost);

    const summaryData: MaterialSummaryData = {
      materialName: this.translate.instant(isSalesTax ? 'PROPERTIES_TAX_SETTINGS_ESTIMATED_SALES_TAX' : 'ESTIMATE_USE_TAX'),
      cost,
      tdcPercent,
      escalation,
      gm,
      sellPrice
    };

    this.consolidatedEstimateSummaryData['tax'] = summaryData;
    const taxRow: IData = this.buildEstimateTotalRow(summaryData);
    const estimateTable = this.findEstimateTotalTable();

    if (estimateTable) {
      estimateTable.data.push(taxRow);
      estimateTable.tableFooter = this.estimateTotalFooterData();
    } else {
      const estimateTotalData: ITableData = {
        title: this.buildSectionTitleRow('ESTIMATE_TOOLS', CONSTANTS.EMPTY_STRING, this.translate.instant('GM_PERCENT')),
        data: [taxRow],
        tableFooter: this.estimateTotalFooterData(),
        isTableBordered: true
      };
      this.materialByTypeData.push(estimateTotalData);
      this.estimateTotalTable = estimateTotalData;
    }
  }

  getEstimateTotalInformation() {
    const estimateDataRows: IData[] = this.formatEstimateToolMaterialDataRows();
    const totalsForHide = this.computeTotals(this.consolidatedEstimateSummaryData);
    if ((estimateDataRows.length === 0 || this.isTotalsZero(totalsForHide))
      && this.solEstimateService.estimateModel.sellAs.sellAsSellPrice === CONSTANTS.ZERO) {
      return;
    }
    const estimateTotalData: ITableData = {
      title: this.buildSectionTitleRow('ESTIMATE_TOTALS', CONSTANTS.EMPTY_STRING, this.translate.instant('GM_PERCENT')),
      data: estimateDataRows,
      tableFooter: this.estimateTotalFooterData(),
      isTableBordered: true
    };
    this.materialByTypeData.push(estimateTotalData);
    this.estimateTotalTable = estimateTotalData;
  }

  private findEstimateTotalTable(): ITableData | undefined {
    const title = this.translate.instant('ESTIMATE_TOTALS');
    return this.estimateTotalTable ?? this.materialByTypeData.find(t => t.title?.[0]?.data === title);
  }

  estimateTotalFooterData(): IFooter[] {
    const model = this.solEstimateService.estimateModel;
    const turboGMPercent = model.sellAs.turboGMPercent;
    const totals = {
      cost: model.subTotals.cost,
      tdcPercent: this.getTdcPercent(model.subTotals.cost),
      gm: model.subTotals.grossMargin,
      sellPrice: model.sellAs.sellAsSellPrice
    };
    return [
      this.buildFooterRow(
        this.translate.instant('ESTIMATE_TOTALS_BOOKIT_GM_CALC'),
        totals,
        CONSTANTS.EMPTY_STRING,
        this.formatPercentage(turboGMPercent)
      )
    ];
  }

  formatEstimateToolMaterialDataRows(): IData[] {
    const summaryEstimateDataRows: IData[] = [];
    this.consolidatedEstimateSummaryData = {};
    const summaryData = this.calculateCostTdcEscalationEstimateTool();
    if (!this.shouldHideRow(summaryData, 'labor')) {
      this.consolidatedEstimateSummaryData['miscellaneousCosts'] = summaryData;
      summaryEstimateDataRows.push(this.buildEstimateTotalRow(summaryData));
    }
    return summaryEstimateDataRows;
  }

  calculateCostTdcEscalationEstimateTool(): MaterialSummaryData {
    const model = this.solEstimateService.estimateModel;
    const tax = model.taxMaterialRate.headerRow;
    const cost = model.subTotals.cost - tax.cost;
    const sellPrice = model.subTotals.sellPrice - tax.sellPrice;
    return {
      materialName: this.translate.instant('ESTIMATE_SUBTOTALS_TDC_INCL_ESCALATION'),
      cost,
      tdcPercent: this.getTdcPercent(cost),
      escalation: cost === CONSTANTS.ZERO ? CONSTANTS.ZERO
        : model.markUpToggle ? model.subTotals.markUpPercentage : model.subTotals.grossMarginPercentage,
      gm: model.subTotals.grossMargin,
      sellPrice
    };
  }
  //#endregion

  //#endregion COST_SUMMARY Report Methods

  ngOnDestroy(): void {
    this.costByPartNumberViewInfoList.length = 0;
    this.networkLaborData.length = 0;
    this.globalAggregatedData.clear();
    this.projectLaborCollection = {};
    this.consolidatedMaterialSummaryData = {};
    this.consolidatedLaborSummaryData = {};
    this.consolidatedMiscSummaryData = {};
    this.consolidatedEstimateSummaryData = {};
    this.consolidatedSubcontractorSummaryData = {};
    this.selectedReportCheckboxes = {} as ReportCheckboxes;
    this.processedTypicalIds.clear();
  }

  private async costByPartNumberTableDataList(): Promise<void> {
    this.networkLaborData.length = 0;
    const laborData = await this.indexDBService.getAllLaborData();
    if (laborData) {
      this.projectLaborCollection = laborData.laborsCollection;
      this.processNetworkLaborInSystems();
      if (this.globalAggregatedData.size > CONSTANTS.ZERO) {
        this.generateCostByPartNumberTable();
      }

      if (this.networkLaborData.length > CONSTANTS.ZERO && !this.selectedReportCheckboxes.expanded.checked) {
        this.generateNetworkLaborTable();
      }
    }
  }

  private processNetworkLaborInSystems(): void {
    for (const [levelId, laborData] of Object.entries(this.projectLaborCollection)) {
      if (!laborData) {
        continue;
      }
      const typicalOf = Object.values(this.typicalOfHelper).find(helper => helper?.typicalOfCollectionHolderId === levelId);
      const multiplier = typicalOf?.typicalOfSystems?.filter(system => system.isItemVisible).length ?? CONSTANTS.ONE;

      if (laborData.conditionsCollection?.length) {
        for (const condition of laborData.conditionsCollection) {
          this.aggregateNetworkLabor(condition, multiplier);
        }
      }

      if (laborData.tasksCollection?.length) {
        for (const task of laborData.tasksCollection) {
          this.aggregateNetworkLabor(task, multiplier);
        }
      }
    }
  }

  private aggregateNetworkLabor(systemChild: Condition | Task, multiplier: number = CONSTANTS.ONE): void {
    const sanitizedData: NetworkLaborData = {
      projectManagementLabor: this.sanitizeValue(systemChild.projectManagementLabor),
      engineerLabor: this.sanitizeValue(systemChild.engineerLabor),
      cyberSecEngineerLabor: this.sanitizeValue(systemChild.cyberSecEngineerLabor),
      energyEngineerLabor: this.sanitizeValue(systemChild.energyEngineerLabor),
      lEngineerLabor: this.sanitizeValue(systemChild.lengEngineerLabor),
      networkEngineerLabor: this.sanitizeValue(systemChild.networkEngineerLabor),
      qualificationEngineerLabor: this.sanitizeValue(systemChild.qualificationEngineerLabor),
      specialistLabor: this.sanitizeValue(systemChild.specialistLabor),
      mechanicalEngineerLabor: this.sanitizeValue(systemChild.mechanicalEngineerLabor),
      electricalEngineerLabor: this.sanitizeValue(systemChild.electricalEngineerLabor),
      graphicsLabor: this.sanitizeValue(systemChild.graphicsLabor),
      digitalDeploymentLabor: this.sanitizeValue(systemChild.digitalDeploymentLabor),
      programmingLabor: this.sanitizeValue(systemChild.programmingLabor)
    };

    for (const category of Object.keys(sanitizedData) as (keyof NetworkLaborData)[]) {
      sanitizedData[category] *= multiplier;
    }

    // Check if any value is non-zero before adding
    if (Object.values(sanitizedData).some(value => value !== CONSTANTS.ZERO)) {
      this.networkLaborData.push(sanitizedData);
    }
  }

  private sanitizeValue(value: number | undefined): number {
    return value === undefined || value === CONSTANTS.NA_LABOR_VALUE ? CONSTANTS.ZERO : value;
  }

  private generateCostByPartNumberTable(): void {
    // Sort the globally aggregated data alphanumerically
    const sortedData = Array.from(this.globalAggregatedData.values()).sort((a, b) =>
      this.compareReferenceNumbers(a.referenceNumber, b.referenceNumber)
    );
    const expanded = this.selectedReportCheckboxes.expanded.checked;
    const tableForCostByPartNumber: ITableData = {
      tableHeader: [],
      title: this.generateCostByPartNumberTitleRow(),
      data: [],
      tableFooter: [],
      isTableBordered: true,
      isPageBreakAfter: false
    };

    // Single pass: build rows AND calculate total
    let totalCost = CONSTANTS.ZERO;
    for (const item of sortedData) {
      const itemTotal = item.quantity * item.cost;
      totalCost += itemTotal;
      tableForCostByPartNumber.data.push({
        data: this.createRow(
          item.referenceNumber,
          item.modelNumber || CONSTANTS.EMPTY_STRING,
          item.manufacturerName || CONSTANTS.EMPTY_STRING,
          item.description,
          item.materialType || CONSTANTS.EMPTY_STRING,
          item.vendorName || CONSTANTS.EMPTY_STRING,
          item.notes || CONSTANTS.EMPTY_STRING,
          item.quantity,
          item.cost,
          itemTotal,
          item.materialIndicator,
        )
      });
    }
    const blankCount = expanded ? 7 : 2;
    const footerHeaders = [
      { headerText: this.translate.instant('TAKE_OFF_TOTAL'), columnspan: CONSTANTS.TWO, alignment: CONSTANTS.LEFT_ALIGNMENT }
    ];
    for (let i = 0; i < blankCount; i++) {
      footerHeaders.push({ headerText: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT });
    }
    footerHeaders.push({ headerText: this.multiLingualPipe.transform(totalCost), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT });
    tableForCostByPartNumber.tableFooter.push({ headers: footerHeaders });

    this.costByPartNumberViewInfoList.push(tableForCostByPartNumber);
  }

  private generateNetworkLaborTable(): void {
    let totalPM = CONSTANTS.ZERO;
    let totalEng = CONSTANTS.ZERO;
    let totalCEng = CONSTANTS.ZERO;
    let totalEEng = CONSTANTS.ZERO;
    let totalLEng = CONSTANTS.ZERO;
    let totalNEng = CONSTANTS.ZERO;
    let totalQEng = CONSTANTS.ZERO;
    let totalSpec = CONSTANTS.ZERO;
    let totalMechEng = CONSTANTS.ZERO;
    let totalElecEng = CONSTANTS.ZERO;
    let totalGraphics = CONSTANTS.ZERO;
    let totalDigital = CONSTANTS.ZERO;
    let totalProgramming = CONSTANTS.ZERO;

    for (const item of this.networkLaborData) {
      totalPM += item.projectManagementLabor;
      totalEng += item.engineerLabor;
      totalCEng += item.cyberSecEngineerLabor;
      totalEEng += item.energyEngineerLabor;
      totalLEng += item.lEngineerLabor;
      totalNEng += item.networkEngineerLabor;
      totalQEng += item.qualificationEngineerLabor;
      totalSpec += item.specialistLabor;
      totalMechEng += item.mechanicalEngineerLabor;
      totalElecEng += item.electricalEngineerLabor;
      totalGraphics += item.graphicsLabor;
      totalDigital += item.digitalDeploymentLabor;
      totalProgramming += item.programmingLabor;
    }

    const laborGroups = [
      {
        labels: ['TOTAL_LABOR', 'PM_LABOR', 'ENG_LABOR', 'CENG', 'EENG', 'LENG', 'NENG'],
        values: [totalPM, totalEng, totalCEng, totalEEng, totalLEng, totalNEng],
        rowLabel: 'ADJUSTED_HOURS'
      },
      {
        labels: ['QENG', 'GRAP', 'PROG', 'SPEC_LABOR', 'DDS', 'MECH_ENG_LABOR', 'ELEC_ENG_LABOR'],
        values: [totalQEng, totalGraphics, totalProgramming, totalSpec, totalDigital, totalMechEng, totalElecEng]
      }
    ];

    const decimalPrecision = this.commonDataService.getDecimalPrecision();
    for (const group of laborGroups) {
      const values = group.values.map(value => this.createDataRow(
        this.decimalPrecisionPipe.transform(value, decimalPrecision),
        CONSTANTS.CENTER_ALIGNMENT
      ));
      if (group.rowLabel) {
        values.unshift(this.createDataRow(
          this.translate.instant(group.rowLabel),
          CONSTANTS.LEFT_ALIGNMENT
        ));
      }

      this.costByPartNumberViewInfoList.push({
        tableHeader: [],
        title: this.generateNetworkLaborTitleRow(group.labels),
        data: [{ data: values }],
        tableFooter: [],
        isTableBordered: true,
        isPageBreakAfter: false
      });
    }
  }

  private getMaterialType(material: string){
    switch(material) {
      case MaterialReportType.Siemens_Material:
      case MaterialReportType.SI_OEM_Material:
      case MaterialReportType.Siemens_Material_FR:
      case MaterialReportType.SI_OEM_Material_FR:
        return 'Siemens';
      case MaterialReportType.Outside_Material:
        return 'Outside';
      case MaterialReportType.Electrical_Material:
        return 'Electrical';
      case MaterialReportType.Mechanical_Material:
        return 'Mechanical';
      default:
        return material;
    }
  }

  private createRow(referenceNumber: string, modelNumber: string,
    manufacturerName: string, description: string,
    material: string, vendor: string, notes: string,
    quantity: number, cost: number, total: number, materialIndicator?: MaterialIndicators): IDataItem[] {

    if (this.selectedReportCheckboxes.expanded.checked) {
      return [
        { data: quantity.toString(), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: referenceNumber, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT, materialIndicator: materialIndicator },
        { data: modelNumber, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT},
        { data: manufacturerName, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: description, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: this.getMaterialType(material), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: vendor, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: notes, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: this.multiLingualPipe.transform(cost), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { data: this.multiLingualPipe.transform(total), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR }
      ];
    } else {
      return [
        { data: referenceNumber, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT, materialIndicator: materialIndicator },
        { data: description, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: quantity.toString(), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: this.multiLingualPipe.transform(cost), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { data: this.multiLingualPipe.transform(total), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR }
      ];
    }
  }

  private generateCostByPartNumberTitleRow(): IDataItem[] {
    const expanded = this.selectedReportCheckboxes?.expanded?.checked ?? false;
    const common = (label: string, width?: string, dataCellColor?: string): IDataItem => ({
      data: this.translate.instant(label),
      columnspan: CONSTANTS.ONE,
      hierarchyLevel: CONSTANTS.ZERO,
      alignment: CONSTANTS.CENTER_ALIGNMENT,
      ...(width ? { width } : {}),
      ...(dataCellColor ? { DataCellColor: dataCellColor } : {})
    });

    if (expanded) {
      return [
        common('Quantity', '30'),
        common('REFERENCE_NUMBER', 'auto'),
        common('MODEL_#', 'auto'),
        common('MANUFACTURER_PART_#', 'auto'),
        common('DESCRIPTION', '150'),
        common('MATERIAL_TYPE', 'auto'),
        common('VENDOR_NAME', 'auto'),
        common('NOTES', '60'),
        common('UNIT_COST', '60'),
        common('TOTAL_COST', '60')
      ];
    }

    return [
      common('REFERENCE_NUMBER', 'auto', CONSTANTS.REPORT_ORANGE_COLOR),
      common('DESCRIPTION', '*'),
      common('Quantity', '30'),
      common('UNIT_COST', '60'),
      common('COST', '60')
    ];
  }

  private generateNetworkLaborTitleRow(labels: string[]): IDataItem[] {
    return labels.map((label, index) => ({
      data: this.translate.instant(label),
      columnspan: CONSTANTS.ONE,
      hierarchyLevel: CONSTANTS.ZERO,
      alignment: index === 0 && label === 'TOTAL_LABOR' ? CONSTANTS.LEFT_ALIGNMENT : CONSTANTS.CENTER_ALIGNMENT,
      width: '*',
      ...(index === 0 && label === 'TOTAL_LABOR' ? { italics: true } : {})
    }));
  }

  /** Builds a 7-column title row for a Cost Summary section. */
  private buildSectionTitleRow(sectionNameKey: string, col2Text: string, col4Text: string): IDataItem[] {
    return [
      { data: this.translate.instant(sectionNameKey), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, italics: true, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR },
      { data: this.translate.instant('MATERIAL_REPORT_TDC_PERCENT'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
      { data: col2Text, isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
      { data: this.translate.instant('MATERIAL_REPORT_COST'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
      { data: col4Text, isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
      { data: this.translate.instant('MATERIAL_REPORT_GM'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
      { data: this.translate.instant('MATERIAL_REPORT_SELL_PRICE'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 }
    ];
  }

  /** Builds a 7-column data row for a Cost Summary section. */
  private buildSectionDataRow(data: MaterialSummaryData, col2Value: string, col4Value: string): IData {
    if (!data) {
      return { data: [] };
    }
    return {
      data: [
        { data: data.materialName, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: this.formatPercentage(data.tdcPercent), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { data: col2Value, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { data: this.multiLingualPipe.transform(data.cost), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { data: col4Value, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { data: this.multiLingualPipe.transform(data.gm), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { data: this.multiLingualPipe.transform(data.sellPrice), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.RIGHT_ALIGNMENT, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR }
      ]
    };
  }

  /** Summary row: empty col2, escalation in col4 (Material section) */
  private buildSummaryRow(data: MaterialSummaryData): IData {
    return this.buildSectionDataRow(data, CONSTANTS.EMPTY_STRING, this.multiLingualPipe.transform(data.escalation));
  }

  /** Labor row: hours in col2, escalation in col4 */
  private buildLaborRow(data: MaterialSummaryData): IData {
    return this.buildSectionDataRow(data, data.hours != null ? this.decimalPrecisionPipe.transform(roundToPrecision(data.hours, this.commonDataService.getDecimalPrecision())) : '', this.multiLingualPipe.transform(data.escalation));
  }

  private buildEstimateTotalRow(data: MaterialSummaryData): IData {
    return this.buildSectionDataRow(data, CONSTANTS.EMPTY_STRING, this.formatPercentage(data.escalation));
  }

  /** Risk/Subcontractor row: empty col2, empty col4 */
  private buildRiskRow(data: MaterialSummaryData): IData {
    return this.buildSectionDataRow(data, CONSTANTS.EMPTY_STRING, CONSTANTS.EMPTY_STRING);
  }

  private formatPercentage(value: number): string {
    const precision = this.commonDataService.getDecimalPrecision();
    return `${this.decimalPrecisionPipe.transform(roundToPrecision(value, precision))}%`;
  }

  /** Builds a 7-column footer row for a Cost Summary section. */
  private buildFooterRow(
    label: string,
    totals: { tdcPercent: number; cost: number; gm: number; sellPrice: number },
    col2Value: string,
    col4Value: string
  ): IFooter {
    return {
      headers: [
        { headerText: label, columnspan: 1 },
        { headerText: this.formatPercentage(totals.tdcPercent), columnspan: 1, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: col2Value, columnspan: 1, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: this.multiLingualPipe.transform(totals.cost), columnspan: 1, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: col4Value, columnspan: 1, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: this.multiLingualPipe.transform(totals.gm), columnspan: 1, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: this.multiLingualPipe.transform(totals.sellPrice), columnspan: 1, alignment: CONSTANTS.RIGHT_ALIGNMENT }
      ]
    };
  }
}
